import React from 'react';
import classNames from 'classnames';

import {useSwitch} from '@react-aria/switch';
import {useToggleState} from '@react-stately/toggle';

import {VisuallyHidden} from '@react-aria/visually-hidden';

import {useHover} from '@react-aria/interactions';
import {useFocusRing} from '@react-aria/focus';

import {TypographySize, useTypography} from '../../../hooks/typography';

import type {Props} from './types';
import {ToggleSize} from './constants';
import styles from './styles.module.css';

export const Toggle = React.forwardRef<HTMLLabelElement, Props>((props, forwardedRef) => {
    const {children, toggleSize = ToggleSize.MEDIUM, isDisabled} = props;
    const ref = React.useRef<HTMLInputElement>(null);
    const state = useToggleState(props);
    const {inputProps} = useSwitch(props, state, ref);
    const {isFocusVisible, focusProps} = useFocusRing();
    const {hoverProps, isHovered} = useHover({isDisabled});
    const labelTypographyCls = useTypography({
        size: TypographySize.BodyL,
    });
    const {isSelected} = state;

    return (
        <label
            {...hoverProps}
            className={classNames(
                styles.container,
                isHovered && styles.hovered,
                isFocusVisible && styles.focused,
                isDisabled && styles.disabled,
                toggleSize && styles[toggleSize],
                isSelected && styles.selected,
            )}
            ref={forwardedRef}
        >
            <VisuallyHidden>
                <input {...inputProps} {...focusProps} ref={ref} />
            </VisuallyHidden>
            <span className={classNames(styles.switch)} />
            {children && <span className={classNames(styles.label, labelTypographyCls)}>{children}</span>}
        </label>
    );
});
